/* iter-53d — Per-currency price overrides editor.
 *
 * Lets the admin pin a psychological price per non-USD currency
 * (e.g. €9.99 instead of the literal FX of $9.99). Values are
 * persisted as minor units in each currency (cents for 2-decimal
 * currencies; whole yen for JPY).
 *
 * Empty/blank inputs clear the override for that currency.
 *
 * Split out of BookstoreAdmin.jsx (iter54) — reused by both the
 * Book editor and the Bundles editor.
 */
import React, { useEffect, useState } from 'react';
import { useCurrency } from '@/contexts/CurrencyContext';

export default function PriceOverridesEditor({ priceCents, overrides, onChange, commitMode = 'change' }) {
  const { supported, convertTo } = useCurrency();
  const [open, setOpen] = useState(
    overrides && Object.keys(overrides).length > 0,
  );
  // iter-53f — Optional commit-on-blur mode for the bundles tab so the
  // edit doesn't fire one PUT per keystroke (caught in code review).
  // We keep `local` as the in-flight draft and only call `onChange`
  // when the input is blurred (commitMode='blur') or on every change
  // (commitMode='change', used by the books editor where onChange is
  // already buffered into form state).
  const [local, setLocal] = useState(overrides || {});
  useEffect(() => { setLocal(overrides || {}); }, [overrides]);
  const effective = commitMode === 'blur' ? local : overrides;
  // Non-USD currencies only — USD is the canonical base.
  const codes = supported.filter((c) => c.code !== 'USD');

  const handleChange = (code, decimals, raw) => {
    const next = { ...(effective || {}) };
    const trimmed = (raw || '').trim();
    if (trimmed === '') {
      delete next[code];
    } else {
      // Parse as natural unit then convert to minor units.
      const num = Number(trimmed.replace(',', '.'));
      if (Number.isNaN(num) || num < 0) return;
      const factor = decimals === 0 ? 1 : 100;
      next[code] = Math.round(num * factor);
    }
    if (commitMode === 'blur') {
      setLocal(next);
    } else {
      onChange(next);
    }
  };

  const commit = () => {
    if (commitMode !== 'blur') return;
    if (JSON.stringify(local) === JSON.stringify(overrides || {})) return;
    onChange(local);
  };

  const formatHint = (code, decimals) => {
    // iter-53f — Convert to *this* currency for the hint, not the
    // active picker currency. Previously every row showed the same
    // active-currency value, which was misleading.
    const meta = supported.find((c) => c.code === code) || { symbol: '' };
    const factor = decimals === 0 ? 1 : 100;
    let converted;
    try {
      const raw = convertTo(priceCents, code);
      converted = (raw / factor).toFixed(decimals);
    } catch { converted = '—'; }
    return `Auto-FX: ${meta.symbol}${converted}`;
  };

  const inputValue = (code, decimals) => {
    if (effective && effective[code] != null) {
      const factor = decimals === 0 ? 1 : 100;
      return (effective[code] / factor).toFixed(decimals);
    }
    return '';
  };

  return (
    <div className="rounded-md border border-border bg-card/40" data-testid="bs-admin-book-overrides">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid="bs-admin-book-overrides-toggle"
        className="flex w-full items-center justify-between px-3 py-2 text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-secondary/40"
      >
        Currency-specific prices
        <span className="text-[10px] font-normal lowercase tracking-normal text-muted-foreground">
          {effective && Object.keys(effective).length
            ? `${Object.keys(effective).length} pinned · click to ${open ? 'collapse' : 'edit'}`
            : open ? 'click to collapse' : 'click to add psychological prices'}
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-2 border-t border-border p-3 sm:grid-cols-2">
          {codes.length === 0 && (
            <div className="text-[11.5px] text-muted-foreground sm:col-span-2">
              Currency list still loading…
            </div>
          )}
          {codes.map((c) => (
            <label key={c.code} className="block">
              <span className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="font-mono-stat">{c.code} ({c.symbol})</span>
                <span className="text-[10px]">{formatHint(c.code, c.decimals)}</span>
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={inputValue(c.code, c.decimals)}
                onChange={(e) => handleChange(c.code, c.decimals, e.target.value)}
                onBlur={commit}
                data-testid={`bs-admin-book-override-${c.code}`}
                placeholder={c.decimals === 0 ? '0' : '0.00'}
                className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] font-mono-stat outline-none focus:border-primary"
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
